import type { StudyRender } from "@-/com.npmjs.haniverse__graphemes/entities";
import graphemeFontsData from "#grapheme-fonts" with { type: "json" };
import onetime from "@-/com.npmjs.onetime";
import {
  allocateVariationSequenceCodePoints,
  type GraphemeFontFamily,
  isGraphemeFontFamily,
  leadingGraphemeUnit,
  type VariationSequenceFontFamily,
} from "#font-families";

/**
 * Grapheme unit → the family that renders it, for every grapheme whose font is NOT the default
 * Noto Sans SC/TC. Absence means "Noto" (or, for a member of `unmapped`, "nothing bundled draws
 * this"), so the common case costs no entry at all.
 *
 * This is a pure projection of the card corpus — each entry is a card's resolved `font` (study
 * YAML override, else the auto-determined TOML value). Because it ships as a module value, a
 * glyph's font is known without a `grapheme.font_family` database read.
 */
export type GraphemeFontsData = {
  /**
   * Font family → the concatenated single-code-point characters it renders. Packed this way (rather
   * than one key per grapheme) to keep the bundled blob ~70 KB instead of ~780 KB.
   */
  fonts: Record<string, string>;
  /** Sparse, authored corrections keyed by the exact display grapheme. */
  render?: Record<string, StudyRender>;
  /**
   * Multi-code-point grapheme units (variation sequences like `"㇔︀"`) → font family. Kept separate
   * because the packed strings above are read code point by code point, which would split a base
   * from its variation selector.
   */
  sequences?: Record<string, GraphemeFontFamily>;
  /** Characters no bundled font contains; these resolve to `null` rather than to Noto. */
  unmapped?: string;
};

const typedGraphemeFonts: GraphemeFontsData = graphemeFontsData;

export { typedGraphemeFonts as graphemeFonts };

export function getGraphemeRender(grapheme: string): StudyRender | undefined {
  return typedGraphemeFonts.render?.[grapheme];
}

/** Characters explicitly flagged as not covered by any bundled font. */
export const getUnmappedCharacters = onetime((): ReadonlySet<string> => {
  const set = new Set<string>();
  for (const character of typedGraphemeFonts.unmapped ?? "") {
    set.add(character);
  }
  return set;
});

/**
 * Every authored variation sequence → the private-use code point `Han Composite` draws its variant
 * at. Allocated deterministically over the corpus projection's `sequences` keys (see
 * `allocateVariationSequenceCodePoints`); the font build performs the identical allocation, so the
 * runtime rewrite and the shipped cmap agree without a shared artifact.
 */
export const getVariationSequenceCodePoints = onetime((): ReadonlyMap<string, number> =>
  allocateVariationSequenceCodePoints(Object.keys(typedGraphemeFonts.sequences ?? {})),
);

/** Grapheme unit → font family, for every non-default grapheme. O(1) lookup. */
export const getGraphemeFontFamilies = onetime((): ReadonlyMap<string, GraphemeFontFamily> => {
  const map = new Map<string, GraphemeFontFamily>();
  for (const [fontFamily, characters] of Object.entries(typedGraphemeFonts.fonts)) {
    if (!isGraphemeFontFamily(fontFamily)) {
      continue;
    }
    // Iterating a string yields whole code points, which is exactly the unit packed above.
    for (const character of characters) {
      map.set(character, fontFamily);
    }
  }
  for (const [sequence, fontFamily] of Object.entries(typedGraphemeFonts.sequences ?? {})) {
    map.set(sequence, fontFamily);
  }
  return map;
});

/**
 * Resolves the non-default font family for a character (or variation sequence) from the corpus
 * projection. A Unicode variation sequence (base + selector, e.g. `"㇆︁"`) is matched as one unit,
 * so the dedicated variation font wins for `㇆︁` while the bare `㇆` still resolves on its own.
 *
 * Returns `null` when the grapheme is unmapped OR is covered by Noto at runtime (the default is not
 * stored); callers apply the script-specific Noto family in that case.
 */
export function resolveGraphemeFontFamily(
  character: string,
): GraphemeFontFamily | VariationSequenceFontFamily | null {
  const graphemeUnit = leadingGraphemeUnit(character);
  const fontFamily = getGraphemeFontFamilies().get(graphemeUnit);
  if (fontFamily !== undefined) {
    return fontFamily;
  }

  if (getUnmappedCharacters().has(character)) {
    return null;
  }

  return null;
}
